import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  saveStoreOrderCommentApi,
  getInvoiceCommentsApi,
  getShippingCommentsApi,
  getCreditMemoCommentsApi
} from 'api';
import { BackdropCircular } from 'components';
import { convertUTCDateToLocalDate, convertDateToUTCString } from 'helpers/date';


const useStyles = makeStyles({
  rootControl: {
    display: 'block',
    margin: '0 0 10px 0',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiButtonBase-root': {
      width: '16px',
      height: '16px',
      borderRadius: 0,
      backgroundColor: '#FFFFFF'
    },
    '& .MuiFormControlLabel-label': {
      marginLeft: '10px'
    }
  },
  rootText: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '5px'
    }
  },
  capture: {
    fontSize: '10px',
    color: '#A7A7A7',
    margin: '5px 0 20px'
  },
  giftMessageContainer: {
    padding: '16px',
    margin: '20px 0',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  giftMessageHeader: {
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333'
  },
  giftMessageField: {
    margin: '4px 0',
    '& strong': {
      color: '#555',
      marginRight: '8px'
    }
  }
});

const OrderNotes = ({order}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const defaultOrderStatus = {
    comment: '',
    created_at: convertDateToUTCString(new Date()),
    is_customer_notified: 0,
    is_visible_on_front: 0,
  };
  const [statusHistories, setStatusHistories] = useState(defaultOrderStatus);
  const {
    status_histories,
    entity_id: orderId,
    extension_attributes = {}
  } = order;
  const giftMessage = extension_attributes?.gift_message || null;

  const {
    invoiceCommentsData,
    loadingInvoiceComments,
    shippingCommentsData,
    loadingShippingComments,
    creditMemoCommentsData,
    loadingCreditMemoComments
  } = useSelector((state: IReduxState) => state.store_orders);

  const combineOrdersComments = [
    ...status_histories,
    ...invoiceCommentsData,
    ...shippingCommentsData,
    ...creditMemoCommentsData
  ];
  const loadingComments = loadingInvoiceComments || loadingShippingComments || loadingCreditMemoComments;
  const sortedComments = orderBy(combineOrdersComments, 'created_at', 'desc');

  const handleSaveComment = async () => {
    await saveStoreOrderCommentApi({
      dispatch,
      orderId,
      comment: { ...statusHistories, status: order.status },
    });
    setStatusHistories(defaultOrderStatus);
  };

  useEffect(() => {
    getInvoiceCommentsApi({dispatch, orderId});
    getShippingCommentsApi({dispatch, orderId});
    getCreditMemoCommentsApi({dispatch, orderId});
  }, []);


  return (
    <>
      <TableContainer>
        <Table className="inheritTable">
          <TableHead>
            <TableRow>
              <TableCell width="10%" />
              <TableCell>Notes For This Order</TableCell>
              <TableCell width="10%" />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell width="10%" />
              <TableCell>
                {giftMessage && (
                  <div className={classes.giftMessageContainer}>
                    <Typography variant="h6" className={classes.giftMessageHeader}>
                      Gift Message Details
                    </Typography>
                    <Divider style={{ marginBottom: '12px' }} />

                    {giftMessage.sender && (
                      <div className={classes.giftMessageField}>
                        <strong>From:</strong> {giftMessage.sender}
                      </div>
                    )}

                    {giftMessage.recipient && (
                      <div className={classes.giftMessageField}>
                        <strong>To:</strong> {giftMessage.recipient}
                      </div>
                    )}

                    {giftMessage.message && (
                      <div className={classes.giftMessageField}>
                        <strong>Message:</strong> {giftMessage.message}
                      </div>
                    )}
                  </div>
                )}

                <div className="my-3">
                  <div className="ml-4">
                    {sortedComments
                      .map((status, index) => {
                        const {comment, created_at} = status;
                        return (
                          <div key={`comment-${index}`}>
                            <div>{comment}</div>
                            <div className={classes.capture}>
                              {convertUTCDateToLocalDate(created_at).toLocaleString(
                                'en-US',
                                {
                                  dateStyle: 'medium',
                                  timeStyle: 'short'
                                }
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <div className="my-5">
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      name="comment"
                      variant="outlined"
                      placeholder="Message..."
                      className={classes.rootText}
                      value={statusHistories.comment}
                      onChange={({target: {name, value}}) => {
                        setStatusHistories({
                          ...statusHistories,
                          [name]: value
                        });
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between flex-wrap">
                    <div>
                      <FormControlLabel
                        className={classes.rootControl}
                        control={
                          <Checkbox
                            checked={Boolean(statusHistories.is_customer_notified)}
                            onChange={({target: {checked}}) => {
                              setStatusHistories({
                                ...statusHistories,
                                is_customer_notified: checked ? 1 : 0
                              });
                            }}
                            name="is_customer_notified"
                            color="primary"
                          />
                        }
                        label="Notify Customer by Email"
                      />
                      <FormControlLabel
                        className={classes.rootControl}
                        control={
                          <Checkbox
                            checked={Boolean(statusHistories.is_visible_on_front)}
                            onChange={({target: {checked}}) => {
                              setStatusHistories({
                                ...statusHistories,
                                is_visible_on_front: checked ? 1 : 0
                              });
                            }}
                            name="is_visible_on_front"
                            color="primary"
                          />
                        }
                        label="Visible on Storefront"
                      />
                    </div>
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSaveComment()}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell width="10%" />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <BackdropCircular loading={loadingComments} />
    </>
  );
};

export default OrderNotes;
